import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const getStoredToken = () =>
    localStorage.getItem("token") || sessionStorage.getItem("token") || null;

  const getStoredUser = () => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser && storedUser !== "undefined"
        ? JSON.parse(storedUser)
        : null;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState(getStoredUser);
  const [token, setToken] = useState(getStoredToken);
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["x-auth-token"] = token;
      fetchUserData();
      fetchCartData();
    } else {
      delete axios.defaults.headers.common["x-auth-token"];
      setUser(null);
      setCartItems(JSON.parse(localStorage.getItem("cartItems") || "[]"));
    }
  }, [token]);

  const fetchUserData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/auth/profile`);
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
    } catch (err) {
      console.error("Error fetching user profile:", err);
      if (err.response?.status === 401) {
        logout();
      }
      const storedUser = getStoredUser();
      setUser(storedUser);
    }
  };

  const fetchCartData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/cart`);
      if (res.data.success) {
        setCartItems(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching cart:", err);
    }
  };

  const login = async (tokenData, userData, remember = false) => {
    try {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");

      const storage = remember ? localStorage : sessionStorage;
      storage.setItem("token", tokenData);
      localStorage.setItem("user", JSON.stringify(userData));

      axios.defaults.headers.common["x-auth-token"] = tokenData;

      setToken(tokenData);
      setUser(userData);

      const guestCart = JSON.parse(localStorage.getItem("cartItems") || "[]");
      if (guestCart.length > 0) {
        try {
          const res = await axios.post(`${API_BASE_URL}/api/cart/sync`, {
            items: guestCart,
          });
          if (res.data.success) {
            setCartItems(res.data.data);
          }
          localStorage.removeItem("cartItems");
        } catch (err) {
          console.error("Error syncing guest cart:", err);
        }
      }

    } catch (err) {
      console.error("Login error in context:", err);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["x-auth-token"];

    setToken(null);
    setUser(null);
    setCartItems([]);
  };

  const addToCart = async (productData, isBuyNow = false) => {
    const existingIndex = cartItems.findIndex(
      (item) =>
        item.productId === productData.productId &&
        item.net_wt === productData.net_wt
    );

    let updatedCart;
    if (existingIndex >= 0) {
      updatedCart = cartItems.map((item, idx) =>
        idx === existingIndex
          ? { ...item, quantity: item.quantity + (productData.quantity || 1) }
          : item
      );
    } else {
      updatedCart = [...cartItems, productData];
    }

    setCartItems(updatedCart);
    if (token) {
      try {
        await axios.put(`${API_BASE_URL}/api/cart`, { items: updatedCart });
      } catch (err) {
        console.error("Error updating DB cart:", err);
      }
    } else {
      localStorage.setItem("cartItems", JSON.stringify(updatedCart));
    }
  };

  const updateCartQuantity = async (index, delta) => {
    if (!cartItems[index]) return;

    const updatedCart = cartItems
      .map((item, idx) =>
        idx === index ? { ...item, quantity: item.quantity + delta } : item
      )
      .filter((item) => item.quantity >= 1);

    setCartItems(updatedCart);

    if (token) {
      try {
        await axios.put(`${API_BASE_URL}/api/cart`, { items: updatedCart });
      } catch (err) {
        console.error("Error updating DB cart:", err);
      }
    } else {
      localStorage.setItem("cartItems", JSON.stringify(updatedCart));
    }
  };

  const removeCartItem = async (index) => {
    let updatedCart = [...cartItems];
    updatedCart.splice(index, 1);
    setCartItems(updatedCart);

    if (token) {
      try {
        await axios.put(`${API_BASE_URL}/api/cart`, { items: updatedCart });
      } catch (err) {
        console.error("Error updating DB cart:", err);
      }
    } else {
      localStorage.setItem("cartItems", JSON.stringify(updatedCart));
    }
  };

  const clearCart = async () => {
    setCartItems([]);
    localStorage.removeItem("cartItems");

    if (token) {
      try {
        await axios.put(`${API_BASE_URL}/api/cart`, { items: [] });
      } catch (err) {
        console.error("Error clearing DB cart:", err);
      }
    }
  };

  return (
    <AppContext.Provider
      value={{
        user,
        token,
        cartItems,
        login,
        logout,
        addToCart,
        updateCartQuantity,
        removeCartItem,
        clearCart,
        setCartItems,
        setUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};